import { useEffect, useState, useMemo, useRef } from "react";
import { api, formatApiError } from "@/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Flag, Repeat, CalendarClock, CheckSquare, Square, Check, Landmark, Trash2, BadgeCheck, Hourglass } from "lucide-react";
import CreateJobDialog from "@/components/CreateJobDialog";
import SearchBar from "@/components/SearchBar";
import { Highlight } from "@/lib/highlight";
import { useBankHolidays } from "@/lib/bankHolidays";
import { useAuth } from "@/context/AuthContext";

// Rolling schedule window. Today lands flush-left on load but users can
// swipe/scroll left to reveal the previous 2 weeks for context (running
// invoices, "did we clean there yesterday?" checks, etc.).
const DAYS_VISIBLE = 60;       // ~8 weeks ahead
const DAYS_BEFORE_TODAY = 14;  // 2 weeks of pannable history

// Names that get a coloured left-border on their job cards on the schedule.
// Business rule (Feb 2026): only Colin/Susan/Michael get the highlight so
// the manager can instantly spot "who's on what" without every card
// screaming for attention. Case-insensitive first-name match.
const BORDERED_FIRST_NAMES = new Set(["colin", "susan", "michael"]);

function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function centeredAnchor(fromDate = new Date()) {
    return addDays(startOfDay(fromDate), -DAYS_BEFORE_TODAY);
}

function startOfWeek(d) {
    const x = new Date(d);
    const day = x.getDay(); // 0=Sun
    const diff = (day + 6) % 7; // make Monday=0
    x.setDate(x.getDate() - diff);
    x.setHours(0, 0, 0, 0);
    return x;
}

function addDays(d, n) {
    const x = new Date(d); x.setDate(x.getDate() + n); return x;
}

function fmtHM(d) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const STATUS_TINTS = {
    scheduled: { bg: "rgba(25,118,210,0.10)", border: "#93C5FD", fg: "#1976D2" },
    in_progress: { bg: "rgba(245,124,0,0.15)", border: "#FBBF24", fg: "#B45309" },
    completed: { bg: "rgba(46,125,50,0.12)", border: "#86EFAC", fg: "#2E7D32" },
    // Finalised = paperwork is done. Rendered fully grey so it's obvious at
    // a glance the job is closed — no colour, no distraction.
    finalised: { bg: "rgba(148,163,184,0.18)", border: "#94A3B8", fg: "#475569" },
    cancelled: { bg: "rgba(107,114,128,0.1)", border: "#D1D5DB", fg: "#4B5563" },
};

// Lightens a hex colour into a soft rgba tint — used to background-fill job
// cards with the client's colour while keeping text readable.
function hexWithAlpha(hex, alpha) {
    if (!hex || !hex.startsWith("#")) return hex;
    const s = hex.slice(1);
    const full = s.length === 3 ? s.split("").map(c => c + c).join("") : s;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}


export default function WeekSchedulePage() {
    const [anchor, setAnchor] = useState(() => centeredAnchor());
    const [jobs, setJobs] = useState([]);
    const [operatives, setOperatives] = useState([]);
    const [clientsById, setClientsById] = useState({});
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dragId, setDragId] = useState(null);
    const [hoverDay, setHoverDay] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [moveTarget, setMoveTarget] = useState(null); // job whose "move" popover is open
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const opById = useMemo(() => Object.fromEntries(operatives.map(o => [o.id, o])), [operatives]);
    const bankHolidays = useBankHolidays();
    const { user: authUser } = useAuth();
    const isManager = authUser?.role === "manager";
    const isOwner = !!authUser?.is_workspace_owner;
    // View mode: "day" = wide 240px columns, "week" = compact 128px columns.
    // Default depends on viewport width — on desktop/laptop (≥1024px) the
    // weekly view is much more useful because you can see the whole crew's
    // week at a glance. On phones we fall back to the daily view so each
    // job card has room to breathe. Once the user picks a mode manually it
    // sticks in localStorage and overrides this default on subsequent loads.
    const [viewMode, setViewMode] = useState(() => {
        try {
            const stored = localStorage.getItem("scs.schedule.viewMode");
            if (stored === "week" || stored === "day") return stored;
        } catch { /* localStorage disabled — fall through to viewport default */ }
        if (typeof window !== "undefined" && window.matchMedia?.("(min-width: 1024px)")?.matches) {
            return "week";
        }
        return "day";
    });
    const scrollRef = useRef(null);
    // Click-and-drag horizontal pan of the schedule (desktop convenience so
    // the manager doesn't have to hunt for the horizontal scrollbar).
    const panState = useRef({ active: false, startX: 0, startScroll: 0 });
    const [isPanning, setIsPanning] = useState(false);

    const onGridMouseDown = (e) => {
        // Left mouse button only. Ignore any click inside an interactive
        // element or a draggable job card.
        if (e.button !== 0) return;
        const el = e.target;
        if (el.closest("button, a, input, label, textarea, select, [role='button'], [draggable='true'], [data-testid^='job-card-']")) return;
        const grid = scrollRef.current;
        if (!grid) return;
        e.preventDefault();
        panState.current = {
            active: true,
            startX: e.clientX,
            startScroll: grid.scrollLeft,
        };
        setIsPanning(true);
    };
    const onGridMouseMove = (e) => {
        if (!panState.current.active) return;
        const grid = scrollRef.current;
        if (!grid) return;
        const dx = e.clientX - panState.current.startX;
        grid.scrollLeft = panState.current.startScroll - dx;
    };
    const onGridMouseUp = () => {
        if (panState.current.active) {
            panState.current.active = false;
            setIsPanning(false);
        }
    };
    const navigate = useNavigate();

    // Column width driven by view mode + screen size.
    // Daily on mobile is 180px so both yesterday and tomorrow peek in on either side of today (better centring feel).
    // Weekly on mobile bumped to 160px so day labels don't feel cramped on a phone.
    // Desktop columns are ~10% larger overall.
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
    // Column width targeted at ~6 days visible on 1440-1600px desktops
    // (main container is ~1240px minus 48px padding). Mobile stays wider so
    // one full day is easy to read/tap on a phone.
    const colPx = viewMode === "week"
        ? (isMobile ? 180 : 190)
        : (isMobile ? 200 : 200);
    // Column height: we now keep every day column TALL so 5-6 jobs fit
    // side-by-side across the schedule; anything beyond that scrolls
    // vertically WITHIN the column (see overflow-y on the inner .space-y-2).
    const colMinH = viewMode === "week"
        ? (isMobile ? 480 : 720)
        : (isMobile ? 560 : 720);

    useEffect(() => {
        try { localStorage.setItem("scs.schedule.viewMode", viewMode); } catch {}
    }, [viewMode]);

    const load = async () => {
        try {
            // Cap the fetch to the schedule's visible 60-day window (+7d
            // back-buffer so the "2 wks back" jump has data ready). Without
            // this the endpoint returns every job the workspace has ever
            // had — including hundreds of materialised recurring
            // occurrences — which is what was making the initial paint slow.
            const from = addDays(anchor, -7);
            const to = addDays(anchor, DAYS_VISIBLE + 7);
            const iso = (d) => d.toISOString();
            const [j, u, c, h] = await Promise.all([
                api.get(`/jobs?since=${encodeURIComponent(iso(from))}&until=${encodeURIComponent(iso(to))}`),
                api.get("/users"),
                api.get("/clients").catch(() => ({ data: [] })),
                api.get("/holidays").catch(() => ({ data: [] })),
            ]);
            setJobs(j.data);
            setOperatives(u.data);
            setClientsById(Object.fromEntries(c.data.map(x => [x.id, x])));
            setHolidays(h.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    // Refetch whenever the visible window changes. Without `anchor` in the
    // deps, jumping back/forward two weeks would leave the schedule stuck
    // on stale data whenever the user navigates outside the initial 74-day
    // fetch window. All other explicit `load()` calls remain intact —
    // this effect just covers the anchor-navigation case.
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [anchor]);

    const days = useMemo(
        () => Array.from({ length: DAYS_VISIBLE }, (_, i) => addDays(anchor, i)),
        [anchor],
    );

    const filteredJobs = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return jobs;
        return jobs.filter(j => {
            const c = clientsById[j.client_id] || {};
            const hay = [
                j.title, j.description, j.notes, j.client_name, j.assigned_to_name, j.status,
                (j.checklist || []).join(" "),
                c.address, c.gate_code, c.alarm_code, c.key_location, c.access_notes, c.parking_notes,
                c.contact_name, c.contact_phone, c.contact_email,
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [jobs, query, clientsById]);

    const jobsByDay = useMemo(() => {
        const map = new Map(days.map(d => [d.toDateString(), []]));
        for (const j of filteredJobs) {
            const d = new Date(j.scheduled_at);
            const key = d.toDateString();
            if (map.has(key)) map.get(key).push(j);
        }
        for (const arr of map.values()) arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        return map;
    }, [filteredJobs, days]);

    const rangeLabel = () => {
        const end = addDays(anchor, DAYS_VISIBLE - 1);
        const sameMonth = anchor.getMonth() === end.getMonth();
        const opts = { month: "short", day: "numeric" };
        return sameMonth
            ? `${anchor.toLocaleDateString(undefined, opts)} – ${end.getDate()}, ${end.getFullYear()}`
            : `${anchor.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}, ${end.getFullYear()}`;
    };


    const bulkShift = async (deltaDays) => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        const shifted = new Map();
        for (const id of ids) {
            const j = jobs.find(x => x.id === id);
            if (!j) continue;
            const dt = new Date(j.scheduled_at);
            dt.setDate(dt.getDate() + deltaDays);
            shifted.set(id, dt.toISOString());
        }
        setJobs(prev => prev.map(j => shifted.has(j.id) ? { ...j, scheduled_at: shifted.get(j.id) } : j));
        try {
            await Promise.all(
                ids.map(id => api.patch(`/jobs/${id}`, { scheduled_at: shifted.get(id) })),
            );
            toast.success(`${ids.length} job${ids.length === 1 ? "" : "s"} shifted ${deltaDays > 0 ? "+" : ""}${deltaDays} day${Math.abs(deltaDays) === 1 ? "" : "s"}`);
            setSelectedIds(new Set());
        } catch (e) {
            toast.error(formatApiError(e));
            load();
        }
    };

    const bulkMoveTo = async (targetDateStr) => {
        // targetDateStr = "YYYY-MM-DD"
        const ids = Array.from(selectedIds);
        if (ids.length === 0 || !targetDateStr) return;
        const [y, m, d] = targetDateStr.split("-").map(Number);
        const moved = new Map();
        for (const id of ids) {
            const j = jobs.find(x => x.id === id);
            if (!j) continue;
            const dt = new Date(j.scheduled_at);
            dt.setFullYear(y, m - 1, d);
            moved.set(id, dt.toISOString());
        }
        setJobs(prev => prev.map(j => moved.has(j.id) ? { ...j, scheduled_at: moved.get(j.id) } : j));
        try {
            await Promise.all(
                ids.map(id => api.patch(`/jobs/${id}`, { scheduled_at: moved.get(id) })),
            );
            const pretty = new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
            toast.success(`${ids.length} job${ids.length === 1 ? "" : "s"} moved to ${pretty}`);
            setSelectedIds(new Set());
        } catch (e) {
            toast.error(formatApiError(e));
            load();
        }
    };

    const bulkFinalise = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        // Optimistic status update
        setJobs(prev => prev.map(j => selectedIds.has(j.id) ? { ...j, status: "finalised" } : j));
        setSelectedIds(new Set());
        try {
            await Promise.all(ids.map(id => api.patch(`/jobs/${id}`, { status: "finalised" })));
            toast.success(`${ids.length} job${ids.length === 1 ? "" : "s"} finalised`);
        } catch (e) {
            toast.error(formatApiError(e));
            load();
        }
    };

    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} job${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
        // Optimistic remove
        setJobs(prev => prev.filter(j => !selectedIds.has(j.id)));
        setSelectedIds(new Set());
        try {
            await Promise.all(ids.map(id => api.delete(`/jobs/${id}`)));
            toast.success(`${ids.length} job${ids.length === 1 ? "" : "s"} deleted`);
        } catch (e) {
            toast.error(formatApiError(e));
            load();  // resync on failure
        }
    };

    const toggleSelect = (jobId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
            return next;
        });
    };

    const moveJobToDate = async (job, targetDate) => {
        const oldDt = new Date(job.scheduled_at);
        const newDt = new Date(targetDate);
        newDt.setHours(oldDt.getHours(), oldDt.getMinutes(), 0, 0);
        if (newDt.toDateString() === oldDt.toDateString()) return;
        setJobs(prev => prev.map(j => j.id === job.id ? { ...j, scheduled_at: newDt.toISOString() } : j));
        try {
            await api.patch(`/jobs/${job.id}`, { scheduled_at: newDt.toISOString() });
            toast.success(`Moved to ${newDt.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}`);
        } catch (e) {
            toast.error(formatApiError(e));
            load();
        }
    };

    const onDrop = async (day) => {
        setHoverDay(null);
        if (!dragId) return;
        const job = jobs.find(x => x.id === dragId);
        setDragId(null);
        if (!job) return;
        await moveJobToDate(job, day);
    };

    const scrollByDays = (n) => {
        const el = scrollRef.current;
        if (!el) return;
        const col = el.querySelector("[data-day-col]");
        const w = col ? col.getBoundingClientRect().width + 12 : 260;
        el.scrollBy({ left: n * w, behavior: "smooth" });
    };

    const jumpBack = () => setAnchor(a => addDays(a, -14));
    const jumpForward = () => setAnchor(a => addDays(a, 14));
    const jumpToday = () => {
        setAnchor(centeredAnchor());
        // scroll today's column into the horizontal center of the scroller
        setTimeout(() => centerTodayColumn(false), 120);
    };

    const centerTodayColumn = (instant = true) => {
        const el = scrollRef.current;
        if (!el) return false;
        const today = startOfDay(new Date()).toDateString();
        const col = el.querySelector(`[data-day-key="${today}"]`);
        if (!col || col.offsetWidth === 0) return false;
        // Snap Today's column flush to the LEFT edge of the visible area.
        // Past 2 weeks sit to the left (pannable / swipeable). The column's
        // offsetLeft is relative to the scroll container's inner content.
        const maxScroll = el.scrollWidth - el.clientWidth;
        const clamped = Math.max(0, Math.min(maxScroll, col.offsetLeft));
        el.scrollTo({ left: clamped, behavior: instant ? "auto" : "smooth" });
        return true;
    };

    // On first load AND whenever the view mode changes, centre today's column.
    // Retry a few times because flex layout / images may still be resolving
    // when the initial paint happens (main cause of the "today at the left" bug).
    useEffect(() => {
        if (loading) return;
        let attempts = 0;
        let raf = null;
        let timer = null;
        const tryCenter = () => {
            attempts += 1;
            const ok = centerTodayColumn(true);
            if (!ok && attempts < 20) {
                timer = setTimeout(() => {
                    raf = requestAnimationFrame(tryCenter);
                }, 40);
            }
        };
        raf = requestAnimationFrame(tryCenter);
        const onLoad = () => centerTodayColumn(true);
        const onResize = () => centerTodayColumn(true);
        window.addEventListener("load", onLoad);
        window.addEventListener("resize", onResize);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            if (timer) clearTimeout(timer);
            window.removeEventListener("load", onLoad);
            window.removeEventListener("resize", onResize);
        };
    }, [loading, viewMode, anchor]);

    return (
        <div className="space-y-4 sm:space-y-6" data-testid="week-schedule-page">
            {/* Header — collapses tightly on mobile */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Schedule</span>
                    <p className="hidden sm:block text-[var(--text-secondary)] mt-2 text-sm">Click & drag any empty area to pan the schedule · drag a job onto another day to reschedule.</p>
                </div>
                <div className="flex items-center gap-2">
                    {isManager && (
                        <button
                            data-testid="bulk-toggle-btn"
                            onClick={() => { setBulkMode(v => !v); setSelectedIds(new Set()); }}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                                bulkMode
                                    ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                    : "text-[var(--text-secondary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                            }`}
                        >
                            <CheckSquare size={14} /> {bulkMode ? "Cancel" : "Select"}
                        </button>
                    )}
                    {isManager && (
                        <button data-testid="week-new-job-btn" onClick={() => setDialogOpen(true)}
                                className="btn-primary inline-flex items-center gap-2">
                            <Plus size={14} /> New job
                        </button>
                    )}
                </div>
            </div>

            {bulkMode && (
                <div
                    data-testid="bulk-shift-bar"
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[var(--pine-light)] border border-[var(--pine)]/30 px-4 py-2.5"
                >
                    <div className="text-sm font-medium text-[var(--pine)]">
                        {selectedIds.size === 0
                            ? "Tap jobs to select them"
                            : `${selectedIds.size} job${selectedIds.size === 1 ? "" : "s"} selected`}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            data-testid="bulk-shift-back-btn"
                            onClick={() => bulkShift(-1)}
                            disabled={selectedIds.size === 0}
                            className="inline-flex items-center gap-1 rounded-full bg-white border border-[var(--pine)]/30 px-3 py-1.5 text-[13px] font-medium text-[var(--pine)] hover:bg-white/70 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={13} /> −1 day
                        </button>
                        <button
                            data-testid="bulk-shift-forward-btn"
                            onClick={() => bulkShift(1)}
                            disabled={selectedIds.size === 0}
                            className="inline-flex items-center gap-1 rounded-full bg-white border border-[var(--pine)]/30 px-3 py-1.5 text-[13px] font-medium text-[var(--pine)] hover:bg-white/70 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            +1 day <ChevronRight size={13} />
                        </button>
                        <label className={`inline-flex items-center gap-1.5 rounded-full bg-white border border-[var(--pine)]/30 px-3 py-1.5 text-[13px] font-medium text-[var(--pine)] cursor-pointer ${selectedIds.size === 0 ? "opacity-40 pointer-events-none" : "hover:bg-white/70"}`}>
                            <CalendarClock size={13} /> Move to
                            <input
                                data-testid="bulk-move-date"
                                type="date"
                                onChange={(e) => { if (e.target.value) { bulkMoveTo(e.target.value); e.target.value = ""; } }}
                                className="bg-transparent outline-none text-[12px] w-[110px]"
                            />
                        </label>
                        {isOwner && (
                            <button
                                data-testid="bulk-finalise-btn"
                                onClick={bulkFinalise}
                                disabled={selectedIds.size === 0}
                                className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <BadgeCheck size={13} /> Finalise
                            </button>
                        )}
                        <button
                            data-testid="bulk-delete-btn"
                            onClick={bulkDelete}
                            disabled={selectedIds.size === 0}
                            className="inline-flex items-center gap-1 rounded-full bg-[var(--terracotta)] px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Trash2 size={13} /> Delete
                        </button>
                    </div>
                </div>
            )}

            {/* Compact single-row control strip: fortnight jumps + Today centre + Daily/Weekly.
                The three jump buttons are hidden on mobile — small screens rely on the
                swipe/drag pan (much faster) and hiding them frees up vertical space for
                the day columns and jobs, which is what really matters on a phone. */}
            <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="week-prev-btn" onClick={jumpBack}
                        className="btn-ghost hidden sm:inline-flex items-center gap-1 text-[13px] !px-3 !py-1.5" aria-label="Previous fortnight">
                    <ChevronLeft size={13} /> 2 wks back
                </button>
                <button data-testid="week-today-btn" onClick={jumpToday}
                        className="btn-ghost hidden sm:inline-flex text-[13px] !px-3 !py-1.5">Today</button>
                <button data-testid="week-next-btn" onClick={jumpForward}
                        className="btn-ghost hidden sm:inline-flex items-center gap-1 text-[13px] !px-3 !py-1.5" aria-label="Next fortnight">
                    2 wks ahead <ChevronRight size={13} />
                </button>
                <div className="ml-auto inline-flex rounded-full border border-[var(--border-soft)] bg-white p-0.5" role="tablist" aria-label="Schedule view">
                    <button
                        data-testid="view-mode-day"
                        role="tab"
                        aria-selected={viewMode === "day"}
                        onClick={() => setViewMode("day")}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                            viewMode === "day"
                                ? "bg-[var(--pine)] text-white"
                                : "text-[var(--text-secondary)] hover:text-black"
                        }`}
                    >
                        Daily
                    </button>
                    <button
                        data-testid="view-mode-week"
                        role="tab"
                        aria-selected={viewMode === "week"}
                        onClick={() => setViewMode("week")}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                            viewMode === "week"
                                ? "bg-[var(--pine)] text-white"
                                : "text-[var(--text-secondary)] hover:text-black"
                        }`}
                    >
                        Weekly
                    </button>
                </div>
            </div>

            <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search jobs — client, address, code…"
                resultLabel={query.trim() ? `${filteredJobs.length} match${filteredJobs.length === 1 ? "" : "es"}` : null}
            />

            {/* Range + day-by-day arrows — the arrows are prominent so operatives can slide days easily */}
            <div className="flex items-center justify-between gap-2">
                <div className="text-xs sm:text-sm text-[var(--text-secondary)] font-medium truncate" data-testid="week-range">{rangeLabel()}</div>
                <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => scrollByDays(-1)}
                            data-testid="scroll-left-btn"
                            className="btn-ghost inline-flex items-center justify-center !p-2 h-9 w-9" aria-label="Scroll one day left">
                        <ChevronLeft size={16} />
                    </button>
                    <button onClick={() => scrollByDays(1)}
                            data-testid="scroll-right-btn"
                            className="btn-ghost inline-flex items-center justify-center !p-2 h-9 w-9" aria-label="Scroll one day right">
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-[var(--text-muted)]">Loading schedule…</div>
            ) : (
                <div
                    ref={scrollRef}
                    className={`relative overflow-x-auto pb-4 -mx-4 sm:-mx-6 px-4 sm:px-6 snap-x scroll-smooth select-none ${
                        isPanning ? "cursor-grabbing" : "cursor-grab"
                    }`}
                    data-testid="week-grid"
                    style={{ scrollbarWidth: "thin" }}
                    onMouseDown={onGridMouseDown}
                    onMouseMove={onGridMouseMove}
                    onMouseUp={onGridMouseUp}
                    onMouseLeave={onGridMouseUp}
                >
                    <div className="flex gap-3 min-w-max">
                        {days.map((d, idx) => {
                            const key = d.toDateString();
                            const dayJobs = jobsByDay.get(key) || [];
                            const isToday = key === new Date().toDateString();
                            const hover = hoverDay === key;
                            // idx offset from today, given DAYS_BEFORE_TODAY (2 wks of history at the front).
                            const daysFromToday = idx - DAYS_BEFORE_TODAY;
                            const weekTag = daysFromToday < -7
                                ? "2 wks ago"
                                : daysFromToday < 0
                                    ? "Last week"
                                    : daysFromToday < 7
                                        ? "This week"
                                        : daysFromToday < 14
                                            ? "Next week"
                                            : daysFromToday < 21
                                                ? "In 2 wks"
                                                : "";
                            // Show the tag whenever we cross a week boundary (idx=0, and every 7 days after).
                            const showWeekTag = daysFromToday === -14 || daysFromToday === -7 || daysFromToday === 0 || daysFromToday === 7 || daysFromToday === 14;
                            // Who's off on this day? Match by inclusive date range.
                            const dayIso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                            const dow = d.getDay(); // 0=Sun, 6=Sat
                            const isWeekend = dow === 0 || dow === 6;
                            const bankHolidayName = bankHolidays[dayIso];
                            // Pale yellow wash for weekends + Irish bank holidays.
                            // Today's own gradient wins so the "you are here"
                            // column still pops on a Saturday.
                            const paleYellow = (isWeekend || bankHolidayName) && !isToday;
                            const offToday = holidays
                                .filter(h => h.start_date <= dayIso && h.end_date >= dayIso)
                                .map(h => {
                                    const u = operatives.find(o => o.id === h.user_id);
                                    return u ? { id: u.id, name: u.name, color: u.color || "", reason: h.reason } : null;
                                })
                                .filter(Boolean);
                            return (
                                <div
                                    key={key}
                                    data-day-col
                                    data-day-key={key}
                                    data-testid={`day-column-${d.toISOString().slice(0,10)}`}
                                    onDragOver={(e) => { e.preventDefault(); setHoverDay(key); }}
                                    onDragLeave={() => setHoverDay(null)}
                                    onDrop={() => onDrop(d)}
                                    className={`card-flat p-2 sm:p-3 shrink-0 snap-start transition-colors ${
                                        hover ? "ring-2 ring-[var(--pine)]" : ""
                                    } ${isToday ? "border-[var(--pine)] ring-1 ring-[var(--pine)]/40 shadow-[0_2px_14px_-6px_rgba(46,125,50,0.35)]" : ""}`}
                                    style={{
                                        width: `${colPx}px`,
                                        minHeight: colMinH,
                                        ...(isToday
                                            ? { background: "linear-gradient(180deg, rgba(46,125,50,0.09), rgba(46,125,50,0.03))" }
                                            : paleYellow
                                                ? { background: "#FFF7D6" }
                                                : {}),
                                    }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">
                                                {d.toLocaleString(undefined, { weekday: "short" })}
                                                {showWeekTag && (
                                                    <span className="ml-1.5 text-[var(--terracotta)]" data-testid={`week-tag-${idx}`}>· {weekTag}</span>
                                                )}
                                                {isToday && (
                                                    <span
                                                        data-testid="today-pill"
                                                        className="ml-1.5 inline-flex items-center rounded-full bg-[var(--pine)] px-1.5 py-[1px] text-[9px] font-bold text-white tracking-wide">
                                                        TODAY
                                                    </span>
                                                )}
                                            </div>
                                            <div className={`font-display text-2xl font-bold leading-none ${isToday ? "text-[var(--pine)]" : ""}`}>
                                                {d.getDate()}
                                                <span className="text-sm text-[var(--text-muted)] font-normal ml-1">
                                                    {d.toLocaleString(undefined, { month: "short" })}
                                                </span>
                                            </div>
                                        </div>
                                        <span className="text-[10px] text-[var(--text-muted)]">{dayJobs.length} job{dayJobs.length===1?"":"s"}</span>
                                    </div>

                                    {bankHolidays[dayIso] && (
                                        <div
                                            data-testid={`bank-holiday-${dayIso}`}
                                            className="mb-2 rounded-md bg-[#FFF7E6] border border-[#F4C69C] px-2 py-1 flex items-center gap-1.5"
                                            title={`Irish bank holiday — ${bankHolidays[dayIso]}. Repeat jobs skip this day automatically.`}>
                                            <Landmark size={11} className="text-[#8A4B22] shrink-0" />
                                            <span className="text-[10px] font-semibold text-[#8A4B22] uppercase tracking-[0.14em] truncate">
                                                {bankHolidays[dayIso]}
                                            </span>
                                        </div>
                                    )}
                                    {offToday.length > 0 && (
                                        <div
                                            data-testid={`off-bar-${dayIso}`}
                                            className="mb-2 rounded-md bg-[var(--surface-soft)] border border-[var(--border-soft)] px-2 py-1.5 space-y-1"
                                            title={offToday.map(o => `${o.name}${o.reason ? " – " + o.reason : ""}`).join(", ")}>
                                            <div className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">Off today</div>
                                            <div className="flex items-center flex-wrap gap-1">
                                                {offToday.map(o => (
                                                    <span
                                                        key={o.id}
                                                        className="inline-flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-white border border-[var(--border-soft)] text-[10px] font-medium max-w-full">
                                                        <span
                                                            className="h-2 w-2 rounded-full shrink-0"
                                                            style={{ background: o.color || "#9ca3af" }} />
                                                        <span className="truncate">{o.name.split(" ")[0]}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div
                                        className="space-y-2 overflow-y-auto pr-1"
                                        style={{ maxHeight: `${colMinH - 90}px` }}
                                    >
                                        {dayJobs.length === 0 && (
                                            <div className="text-xs text-[var(--text-muted)] italic py-4 text-center border border-dashed border-[var(--border-soft)] rounded-lg">
                                                Drop here
                                            </div>
                                        )}
                                        {dayJobs.map(job => {
                                            const statusTint = STATUS_TINTS[job.status] || STATUS_TINTS.scheduled;
                                            const client = clientsById[job.client_id];
                                            const clientColor = client?.color;
                                            // Finalised jobs are shown fully greyscale — the manager's
                                            // asked for a clear visual "this is closed, don't touch it"
                                            // signal. Any client-colour background wash is dropped for
                                            // finalised so the whole card reads grey.
                                            const isFinalised = job.status === "finalised";
                                            const bg = isFinalised
                                                ? statusTint.bg
                                                : (clientColor ? hexWithAlpha(clientColor, 0.18) : statusTint.bg);
                                            const border = statusTint.border;
                                            const fg = (!isFinalised && clientColor) ? "#1f2937" : statusTint.fg;
                                            const active = job.clocked_in_at && !job.clocked_out_at;
                                            const isMoveOpen = moveTarget?.id === job.id;
                                            const assignee = job.assigned_to ? opById[job.assigned_to] : null;
                                            const assigneeColor = assignee?.color || "";
                                            const isSelected = selectedIds.has(job.id);
                                            // Highlight border: ONLY when the PRIMARY assignee's first
                                            // name is Colin / Susan / Michael. Helpers no longer trigger
                                            // the border — a job assigned to another operative stays on
                                            // the default status border even if Colin is helping out.
                                            const firstName = (name) => (name || "").trim().toLowerCase().split(/\s+/)[0] || "";
                                            // Highlight border only for Colin / Susan / Michael's active
                                            // jobs. Once finalised, everything goes grey — no personal
                                            // colour, no highlight.
                                            const highlightBorder = (!isFinalised && BORDERED_FIRST_NAMES.has(firstName(assignee?.name)))
                                                ? assigneeColor
                                                : "";
                                            return (
                                                <div
                                                    key={job.id}
                                                    draggable={isManager && !bulkMode}
                                                    data-testid={`week-job-${job.id}`}
                                                    onDragStart={() => setDragId(job.id)}
                                                    onDragEnd={() => { setDragId(null); setHoverDay(null); }}
                                                    onClick={() => {
                                                        if (bulkMode) { toggleSelect(job.id); return; }
                                                        navigate(`/jobs/${job.id}`);
                                                    }}
                                                    className={`relative overflow-hidden rounded-lg p-2.5 border-2 transition-transform hover:-translate-y-0.5 ${
                                                        bulkMode ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"
                                                    } ${isSelected ? "ring-2 ring-[var(--pine)] shadow-md" : ""} ${
                                                        job.status === "completed" || job.status === "finalised" ? "opacity-70" : ""
                                                    }`}
                                                    style={{
                                                        background: bg,
                                                        borderColor: highlightBorder || border,
                                                        boxShadow: highlightBorder ? `inset 0 0 0 1px ${highlightBorder}22` : undefined,
                                                    }}
                                                >
                                                    {/* Assignee corner triangle — shown for EVERY operative
                                                        who has a colour set. Suppressed on finalised jobs so
                                                        the card reads fully grey. */}
                                                    {assigneeColor && !isFinalised && (
                                                        <span
                                                            data-testid={`assignee-triangle-${job.id}`}
                                                            aria-hidden
                                                            className="pointer-events-none absolute top-0 left-0"
                                                            style={{
                                                                width: 0,
                                                                height: 0,
                                                                borderTop: `14px solid ${assigneeColor}`,
                                                                borderRight: "14px solid transparent",
                                                            }}
                                                            title={assignee?.name || ""}
                                                        />
                                                    )}
                                                    {/* Completed tick — top-right */}
                                                    {job.status === "completed" && (
                                                        <span
                                                            data-testid={`completed-tick-${job.id}`}
                                                            aria-hidden
                                                            className="pointer-events-none absolute top-1 right-1 h-4 w-4 rounded-full bg-[var(--pine)] text-white flex items-center justify-center shadow-sm"
                                                            title="Completed"
                                                        >
                                                            <Check size={10} strokeWidth={3} />
                                                        </span>
                                                    )}
                                                    {/* Finalised tick — top-right (indigo, double-check meaning "paperwork done") */}
                                                    {job.status === "finalised" && (
                                                        <span
                                                            data-testid={`finalised-tick-${job.id}`}
                                                            aria-hidden
                                                            className="pointer-events-none absolute top-1 right-1 h-4 w-4 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-sm ring-2 ring-white"
                                                            title="Finalised — paperwork done"
                                                        >
                                                            <Check size={10} strokeWidth={3} />
                                                        </span>
                                                    )}
                                                    <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wide" style={{ color: fg }}>
                                                        {bulkMode && (
                                                            <span className={`inline-flex items-center justify-center h-3.5 w-3.5 rounded-sm border transition-colors ${
                                                                isSelected ? "bg-[var(--pine)] border-[var(--pine)] text-white" : "bg-white border-[var(--text-muted)]"
                                                            }`}>
                                                                {isSelected && <CheckSquare size={9} />}
                                                            </span>
                                                        )}
                                                        {fmtHM(new Date(job.scheduled_at))}
                                                        {job.priority === "high" && <Flag size={10} className="text-[var(--terracotta)]" />}
                                                        {job.recurrence_days > 0 && <Repeat size={10} />}
                                                        {job.wip_copy_id && job.status !== "finalised" && (
                                                            <Hourglass size={10} className="text-amber-600" data-testid={`wip-icon-${job.id}`} aria-label="Also in Work in progress" />
                                                        )}
                                                        {active && (
                                                            <span className="ml-auto inline-flex items-center gap-1 text-[9px] font-bold uppercase text-orange-700">
                                                                <span className="relative flex h-1.5 w-1.5">
                                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-60"></span>
                                                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-500"></span>
                                                                </span>
                                                                Live
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className={`font-semibold mt-1 line-clamp-2 leading-tight ${viewMode === "week" ? "text-[11px]" : "text-sm"}`}>
                                                        <Highlight text={job.address || job.title} query={query} />
                                                    </div>
                                                    <div className={`text-[var(--text-secondary)] mt-0.5 truncate ${viewMode === "week" ? "text-[10px]" : "text-[11px]"}`}>
                                                        <Highlight text={job.client_name || "—"} query={query} />
                                                    </div>
                                                    {viewMode !== "week" && (
                                                        <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 truncate">
                                                            {job.assigned_to_name
                                                                ? <Highlight text={job.assigned_to_name} query={query} />
                                                                : <span className="italic text-[var(--text-muted)]">Unassigned</span>}
                                                        </div>
                                                    )}

                                                    {/* Move-to-another-day button — hidden on completed/finalised jobs so it doesn't collide with the tick badge */}
                                                    {job.status !== "completed" && job.status !== "finalised" && (
                                                        <button
                                                            type="button"
                                                            data-testid={`move-job-btn-${job.id}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMoveTarget(isMoveOpen ? null : job);
                                                            }}
                                                            title="Move to another day"
                                                            aria-label="Move to another day"
                                                            className="absolute top-1.5 right-1.5 p-1 rounded-md bg-white/70 hover:bg-white text-[var(--text-secondary)] hover:text-[var(--pine)] transition-colors">
                                                            <CalendarClock size={12} />
                                                        </button>
                                                    )}

                                                    {isMoveOpen && (
                                                        <MoveDatePopover
                                                            job={job}
                                                            onCancel={() => setMoveTarget(null)}
                                                            onPick={async (d) => {
                                                                setMoveTarget(null);
                                                                await moveJobToDate(job, d);
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <CreateJobDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onCreated={() => load()}
                operatives={operatives}
            />
        </div>
    );
}


function MoveDatePopover({ job, onPick, onCancel }) {
    // Native date input keeps this simple across iOS/Android/desktop.
    const initial = new Date(job.scheduled_at);
    const isoDay = `${initial.getFullYear()}-${String(initial.getMonth()+1).padStart(2,"0")}-${String(initial.getDate()).padStart(2,"0")}`;
    const [value, setValue] = useState(isoDay);
    return (
        <div
            data-testid={`move-popover-${job.id}`}
            onClick={(e) => e.stopPropagation()}
            className="absolute z-30 top-full right-0 mt-1 bg-white border border-[var(--border-soft)] rounded-lg shadow-lg p-2 flex items-center gap-2"
        >
            <input
                type="date"
                autoFocus
                data-testid={`move-date-input-${job.id}`}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="text-xs px-2 py-1 border border-[var(--border-soft)] rounded"
            />
            <button
                type="button"
                data-testid={`move-confirm-${job.id}`}
                onClick={() => {
                    if (!value) return;
                    const [y, m, d] = value.split("-").map(Number);
                    onPick(new Date(y, m - 1, d));
                }}
                className="text-xs font-semibold text-white bg-[var(--pine)] hover:opacity-90 px-2 py-1 rounded"
            >
                Move
            </button>
            <button
                type="button"
                data-testid={`move-cancel-${job.id}`}
                onClick={onCancel}
                className="text-xs text-[var(--text-muted)] hover:text-black px-1"
                aria-label="Cancel">
                ✕
            </button>
        </div>
    );
}
